const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const handle = async (res, fallbackMsg) => {
  if (!res.ok) {
    let msg = fallbackMsg;
    try {
      const body = await res.json();
      if (body?.message) msg = body.message;
    } catch (_) { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
};

/**
 * Crea una liquidación en estado Borrador a partir de las entregas
 * confirmadas de un cliente en un rango de fechas.
 */
export const crearLiquidacion = async (data) => {
  const res = await fetch(`${API_URL}/liquidaciones-clientes/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handle(res, 'Error al crear la liquidación');
};

/**
 * Lista liquidaciones con paginación y filtros.
 * body = { query, page, limit }
 */
export const filterLiquidaciones = async ({ query = {}, page = 1, limit = 20 } = {}) => {
  const res = await fetch(`${API_URL}/liquidaciones-clientes/filter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, page, limit }),
  });
  return handle(res, 'Error al listar las liquidaciones');
};

/**
 * Detalle de una liquidación por id.
 */
export const getLiquidacionById = async (id) => {
  const res = await fetch(`${API_URL}/liquidaciones-clientes/${id}`);
  return handle(res, 'Error al obtener la liquidación');
};

/**
 * Preview: muestra las entregas pendientes de liquidar para un cliente
 * y un rango de fechas, con el detalle de items y precios sugeridos.
 * NO crea la liquidación.
 */
export const previewPendientes = async (clienteId, fechaDesde, fechaHasta) => {
  const params = new URLSearchParams();
  if (fechaDesde) params.append('fechaDesde', fechaDesde);
  if (fechaHasta) params.append('fechaHasta', fechaHasta);
  const qs = params.toString();
  const url = `${API_URL}/liquidaciones-clientes/preview/${clienteId}${qs ? `?${qs}` : ''}`;
  const res = await fetch(url);
  return handle(res, 'Error al obtener el preview de liquidaciones');
};

/**
 * Cambia el nombre y/u observaciones de una liquidación en Borrador.
 */
export const updateMetadataLiquidacion = async (id, data) => {
  const res = await fetch(`${API_URL}/liquidaciones-clientes/metadata/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handle(res, 'Error al actualizar la liquidación');
};

/**
 * Modifica el precio unitario de un item específico.
 */
export const updatePrecioItem = async (id, payload) => {
  const res = await fetch(`${API_URL}/liquidaciones-clientes/precio-item/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handle(res, 'Error al actualizar el precio');
};

/**
 * Modifica varios precios en una sola llamada.
 */
export const updatePreciosBatch = async (id, items) => {
  const res = await fetch(`${API_URL}/liquidaciones-clientes/precios-batch/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  return handle(res, 'Error al actualizar los precios');
};

/**
 * Confirma la liquidación: marca las entregas como liquidadas.
 */
export const confirmarLiquidacion = async (id, usuario) => {
  const res = await fetch(`${API_URL}/liquidaciones-clientes/confirmar/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ usuario }),
  });
  return handle(res, 'Error al confirmar la liquidación');
};

/**
 * Anula la liquidación y libera las entregas.
 */
export const anularLiquidacion = async (id, motivo, usuario) => {
  const res = await fetch(`${API_URL}/liquidaciones-clientes/anular/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ motivo, usuario }),
  });
  return handle(res, 'Error al anular la liquidación');
};

/**
 * Devuelve la última liquidación por cada cliente (útil para listas
 * de selección). Cada item trae: clienteId, clienteNombre, lastConsecutivo,
 * lastEstado, lastTotal, lastFechaCreacion, totalLiquidaciones.
 */
export const getUltimaPorCliente = async () => {
  const res = await fetch(`${API_URL}/liquidaciones-clientes/ultima-por-cliente`);
  return handle(res, 'Error al obtener últimas liquidaciones por cliente');
};
